import { VariantProps, cva } from 'class-variance-authority';
import { DetailedHTMLProps, FC, ForwardedRef, HTMLAttributes, ReactNode, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import styles from './Chip.module.scss';

const chipVariants = cva(['inline-flex items-center', 'rounded-full', 'font-medium', 'whitespace-nowrap'], {
    variants: {
        color: {
            primary: [],
            secondary: [],
            success: [],
            danger: [],
            warning: [],
        },
        variant: {
            default: [],
            shaded: [styles['shaded-elevation']],
            outlined: [],
        },
        size: {
            sm: ['text-[11px] px-[6px] h-6'],
            normal: ['text-xs px-2 h-7'],
            lg: ['text-sm px-[10px] h-8'],
        },
    },
    compoundVariants: [
        // primary
        {
            color: 'primary',
            variant: 'default',
            className: ['bg-primary', 'text-white'],
        },
        {
            color: 'primary',
            variant: 'shaded',
            className: ['bg-primary/10', 'text-primary'],
        },
        {
            color: 'primary',
            variant: 'outlined',
            className: ['border border-primary', 'text-primary'],
        },
        // secondary
        {
            color: 'secondary',
            variant: 'default',
            className: ['bg-secondary', 'text-secondary-contrast'],
        },
        {
            color: 'secondary',
            variant: 'shaded',
            className: ['bg-secondary/10', 'text-secondary'],
        },
        {
            color: 'secondary',
            variant: 'outlined',
            className: ['border border-secondary', 'text-secondary'],
        },
        // success
        {
            color: 'success',
            variant: 'default',
            className: ['bg-success', 'text-white'],
        },
        {
            color: 'success',
            variant: 'shaded',
            className: ['bg-success/10', 'text-success'],
        },
        {
            color: 'success',
            variant: 'outlined',
            className: ['border border-success', 'text-success'],
        },
        // danger
        {
            color: 'danger',
            variant: 'default',
            className: ['bg-danger', 'text-white'],
        },
        {
            color: 'danger',
            variant: 'shaded',
            className: ['bg-danger/10', 'text-danger'],
        },
        {
            color: 'danger',
            variant: 'outlined',
            className: ['border border-danger', 'text-danger'],
        },
        // warning
        {
            color: 'warning',
            variant: 'default',
            className: ['bg-warning', 'text-white'],
        },
        {
            color: 'warning',
            variant: 'shaded',
            className: ['bg-warning/10', 'text-warning'],
        },
        {
            color: 'warning',
            variant: 'outlined',
            className: ['border border-warning', 'text-warning'],
        },
    ],
    defaultVariants: {
        color: 'primary',
        variant: 'default',
        size: 'normal',
    },
});

interface ChipProps
    extends Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'color'>,
        VariantProps<typeof chipVariants> {
    label: ReactNode;
    labelClassName?: string;
    icon?: ReactNode;
    onDelete?: () => void;
    ref?: ForwardedRef<HTMLDivElement>;
}

const Chip = forwardRef<HTMLDivElement, ChipProps>((props: ChipProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { label, labelClassName, icon, color, variant, size, onDelete, className, ...rest } = props;

    return (
        <div {...rest} ref={ref} className={twMerge(chipVariants({ color, variant, size, className }))}>
            {icon}

            <span className={twMerge('mx-1', labelClassName)}>{label}</span>

            {onDelete && (
                <button type="button" onClick={onDelete}>
                    <svg
                        width={'1.2em'}
                        height={'1.2em'}
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M11.782 4.032a.575.575 0 10-.813-.814L7.5 6.687 4.032 3.218a.575.575 0 00-.814.814L6.687 7.5l-3.469 3.468a.575.575 0 00.814.814L7.5 8.313l3.469 3.469a.575.575 0 00.813-.814L8.313 7.5l3.469-3.468z"
                            fill="currentColor"
                            fillRule="evenodd"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
            )}
        </div>
    );
});

export default Chip;
